import {
    concatClassNames,
    getStyleVariantClass,
} from "@thelabnyc/thelabui/src/utils/styles";

import styles from "../styles/composable/star-rating.module.scss";
import type { ClassNames as SealyClassNames } from "../styles/composable/star-rating.sealy.module.scss";
import type { ClassNames as TempurpedicClassNames } from "../styles/composable/star-rating.tempurpedic.module.scss";

/**
 * Every star rating class exists only in a site variant of the module (the
 * base is neutral). `styles` is already the site's variant at module load (the
 * build swaps the import), so each resolves to the hashed class on sealy and
 * tempurpedic and `undefined` elsewhere. The keys are typed against the
 * variant `.d.ts` files.
 */
const variantClass = (
    key: SealyClassNames | TempurpedicClassNames,
): string | undefined => getStyleVariantClass<string>(styles, key);

// Each constant pairs its module class with the literal `star-rating*` hook.
// The literals stay: site partials and libs modules hold contextual rules on
// them (`.card .star-rating__star`, `[class~="star-rating__star--outline"]`,
// …), and toppers queries `.star-rating__star` from JS.

export const STAR_RATING_CLASS_NAME = concatClassNames([
    variantClass("starRating"),
    "star-rating",
]);

export const STAR_RATING_INNER_CONTAINER_CLASS_NAME = concatClassNames([
    variantClass("starRatingInnerContainer"),
    "star-rating__inner-container",
]);

export const STAR_RATING_STARS_CLASS_NAME = concatClassNames([
    variantClass("starRatingStars"),
    "star-rating__stars",
]);

export const STAR_RATING_STAR_CLASS_NAME = concatClassNames([
    variantClass("starRatingStar"),
    "star-rating__star",
]);

// Modifiers RatingGraphic applies from its star state, beside
// STAR_RATING_STAR_CLASS_NAME.
export const STAR_RATING_STAR_HALF_OUTLINE_CLASS_NAME = concatClassNames([
    variantClass("starRatingStarHalfOutline"),
    "star-rating__star--half-outline",
]);

export const STAR_RATING_STAR_OUTLINE_CLASS_NAME = concatClassNames([
    variantClass("starRatingStarOutline"),
    "star-rating__star--outline",
]);

/** The gradient `<stop>` RatingGraphic renders for half stars. */
export const STAR_RATING_STAR_HALF_STAR_COLOR_CLASS_NAME = concatClassNames([
    variantClass("starRatingStarHalfStarColor"),
    "star-rating__star--half-star-color",
]);

export const STAR_RATING_NUMBER_CLASS_NAME = concatClassNames([
    variantClass("starRatingNumber"),
    "star-rating__number",
]);
